import type { DemolitionFxController } from '../fallback/ImpactFx';
import type { PowderResidueStamp } from './PowderResidueLayer';
import type {
  CanonicalDemolitionEventUnion,
  DemolitionImpactBurstPayload,
  DemolitionImpactTracePayload,
  DemolitionShardPayload,
} from '../../structures/demolition/demolitionEventRing';
import type { DamageFact } from '../../structures/demolition/demolitionDamageLedger';
import type { ProfileId } from '../../services/destructionProfiles';
import type { ShardParticleSpawn } from '../../structures/demolition/demolitionPhysics';

export interface DemolitionPresentationResidueSink {
  stamp(input: PowderResidueStamp): void;
}

export interface DemolitionPresentationDispatchOptions {
  /** Stable application address for diagnostics and future scene routing. */
  readonly structureKey: string;
  /** Material authority for this structure; event payload materials are not trusted for routing. */
  readonly material: ProfileId;
  /** World-space center used to lift damage facts from wall-local coordinates. */
  readonly wallCenter: { readonly x: number; readonly y: number; readonly z: number };
  readonly events: readonly CanonicalDemolitionEventUnion[];
  readonly effects?: DemolitionFxController | null;
  readonly residue?: DemolitionPresentationResidueSink | null;
  /** Disabled effects must not mutate or reinterpret authoritative event input. */
  readonly effectsEnabled?: boolean;
}

export interface DemolitionPresentationDispatchTelemetry {
  readonly structureKey: string;
  readonly epoch: number | null;
  /** Whether presentation sinks were eligible to receive this batch. */
  readonly effectsEnabled: boolean;
  readonly eventsSeen: number;
  readonly shards: number;
  readonly shockwaves: number;
  readonly impactBursts: number;
  readonly impactTraces: number;
  readonly landingPuffs: number;
  readonly pulverizedClouds: number;
  readonly residueStamps: number;
  readonly pulverizedVolume: number;
}

/**
 * Preserve legacy effect seeds in epoch zero while making post-reset sequence
 * reuse deterministic and unambiguous. The ring capacity is far below this
 * stride, so an epoch cannot collide with another epoch's sequence range.
 */
export function demolitionPresentationSeed(epoch: number, sequence: number): number {
  return epoch === 0 ? sequence : epoch * 1_000_000 + sequence;
}

function materializedBurst(payload: DemolitionImpactBurstPayload, material: ProfileId): DemolitionImpactBurstPayload {
  return { ...payload, material };
}

function materializedShard(payload: DemolitionShardPayload, material: ProfileId): ShardParticleSpawn {
  return { ...payload, material };
}

function materializedTrace(payload: DemolitionImpactTracePayload, material: ProfileId): DemolitionImpactTracePayload {
  return { ...payload, material };
}

/**
 * Route one already-drained canonical batch to presentation sinks. This
 * adapter owns no queue and never drains; the caller remains the sole reader.
 */
export function dispatchDemolitionPresentation(
  options: DemolitionPresentationDispatchOptions,
): DemolitionPresentationDispatchTelemetry {
  const effectsEnabled = options.effectsEnabled !== false;
  const effects = effectsEnabled ? options.effects ?? null : null;
  const residue = effectsEnabled ? options.residue ?? null : null;
  // Effects-off still records deterministic routing telemetry, but allocates
  // no presentation payload array and performs no payload materialization.
  const pendingShards: ShardParticleSpawn[] | null = effects ? [] : null;
  let firstEpoch: number | null = null;
  let mixedEpoch = false;
  let shards = 0;
  let shockwaves = 0;
  let impactBursts = 0;
  let impactTraces = 0;
  let landingPuffs = 0;
  let pulverizedClouds = 0;
  let residueStamps = 0;
  let pulverizedVolume = 0;

  for (const event of options.events) {
    if (firstEpoch === null) firstEpoch = event.epoch;
    else if (firstEpoch !== event.epoch) mixedEpoch = true;
    const seed = demolitionPresentationSeed(event.epoch, event.sequence);
    if (event.kind === 'shard') {
      shards += 1;
      pendingShards?.push(materializedShard(event.payload, options.material));
      continue;
    }
    if (event.kind === 'shockwave') {
      shockwaves += 1;
      effects?.spawnShockwave(
        event.payload.x,
        event.payload.y,
        event.payload.z,
        event.payload.radius,
        seed,
        options.material,
      );
      continue;
    }
    if (event.kind === 'impact-burst') {
      impactBursts += 1;
      if (effects) {
        effects.spawnImpactBurst(materializedBurst(event.payload, options.material), seed);
      }
      continue;
    }
    if (event.kind === 'impact-trace') {
      impactTraces += 1;
      const payload = event.payload;
      if (effects) {
        effects.spawnTraceReleases(materializedTrace(payload, options.material), seed);
      }
      if (payload.topologyChanged) {
        const releases = payload.supportReleases.length > 0
          ? payload.supportReleases
          : payload.directReleases;
        for (let i = 0; i < Math.min(releases.length, 8); i += 1) {
          const release = releases[i];
          landingPuffs += 1;
          effects?.spawnLandingPuff(
            release.position.x,
            release.position.y,
            release.position.z,
            seed + i,
            options.material,
          );
        }
      }
      continue;
    }
    if (event.kind !== 'damage-fact') continue;

    const fact = event.payload as DamageFact;
    const volume = fact.pulverizedVolume ?? 0;
    if (!(volume > 1e-8)) continue;
    pulverizedVolume += volume;
    pulverizedClouds += 1;
    const position = fact.position;
    const worldX = position.x + options.wallCenter.x;
    const worldY = position.y + options.wallCenter.y;
    const worldZ = position.z + options.wallCenter.z;
    effects?.spawnPulverizedCloud(
      worldX,
      worldY,
      worldZ,
      fact.outwardNormal,
      volume,
      seed,
      options.material,
    );
    residueStamps += 1;
    residue?.stamp({
      x: worldX,
      z: worldZ,
      volume,
      material: options.material,
      rest: [],
    });
  }

  if (pendingShards && pendingShards.length > 0) effects?.spawnShards(pendingShards);

  return Object.freeze({
    structureKey: options.structureKey,
    epoch: mixedEpoch ? null : firstEpoch,
    effectsEnabled,
    eventsSeen: options.events.length,
    shards,
    shockwaves,
    impactBursts,
    impactTraces,
    landingPuffs,
    pulverizedClouds,
    residueStamps,
    pulverizedVolume,
  });
}
